import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  GlobalStyles,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  Security,
  VerifiedUser,
  CheckCircle,
  FiberManualRecord,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import api from '../services/api';

/**
 * ── DESIGN TOKENS ─────────────────────────────────────────────────────────
 * Same as Login page - locked to light appearance with premium aesthetic
 */
const palette = {
  ink: '#0B1220',
  inkSoft: '#2A3342',
  muted: '#5B6472',
  faint: '#8A93A3',
  paper: '#FBFAF7',
  card: '#FFFFFF',
  border: '#E7E3D8',
  ledger: '#0E1B2E',
  ledgerLine: 'rgba(255,255,255,0.08)',
  teal: '#0F766E',
  tealDark: '#0B5C56',
  tealSoft: 'rgba(15,118,110,0.10)',
  amber: '#D97706',
  red: '#DC2626',
  green: '#16A34A',
};

const lightOnlyTheme = createTheme({
  palette: {
    mode: 'light',
    background: { default: palette.paper, paper: palette.card },
    text: { primary: palette.ink, secondary: palette.muted },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
});

export default function ResetPassword() {
  const isMobile = useMediaQuery('(max-width:960px)');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token'); // 👈 ADD THIS LINE

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '1rem',
      color: palette.ink,
      bgcolor: palette.card,
      '& input': { py: 1.85 },
      '& input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 100px ${palette.card} inset`,
        WebkitTextFillColor: palette.ink,
      },
      '& fieldset': { borderColor: palette.border },
      '&:hover fieldset': { borderColor: '#C7C1B0' },
      '&.Mui-focused fieldset': { borderColor: palette.teal, borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root': { color: palette.muted, fontSize: '0.95rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: palette.tealDark },
  };

  // ─── Ledger Data (Same as Login) ─────────────────────────────────────────
  const ledger = [
    { time: '09:41:03', vendor: 'Meridian Cloud Services', delta: -12, tag: 'CVE-2026-11284 disclosed' },
    { time: '09:38:47', vendor: 'Northwind Payments', delta: 4, tag: 'SOC 2 Type II renewed' },
    { time: '09:22:15', vendor: 'Argosy Data Labs', delta: -6, tag: 'TLS cert expires in 14d' },
    { time: '09:05:52', vendor: 'Halcyon Logistics', delta: -3, tag: 'Subprocessor added' },
  ];

  // ─── Validate Token ──────────────────────────────────────────────────────
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenError('No reset token provided. Please request a new password reset.');
        setValidating(false);
        return;
      }

      try {
        const response = await api.post('/v1/password/validate-token', null, {
          params: { token }
        });
        
        if (response.data.valid) {
          setTokenValid(true);
          setEmail(response.data.email || '');
        } else {
          setTokenValid(false);
          setTokenError(response.data.message || 'Invalid or expired reset token.');
        }
      } catch (err: any) {
        setTokenValid(false);
        const d = err.response?.data?.detail;
        setTokenError(typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : 'Failed to validate token. Please request a new password reset.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // ─── Handle Password Reset ──────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/v1/password/reset', {
        token: token,
        new_password: newPassword,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Loading State ──────────────────────────────────────────────────────
  if (validating) {
    return (
      <ThemeProvider theme={lightOnlyTheme}>
        <GlobalStyles styles={{
          '@import': "url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap')",
          ':root': { colorScheme: 'light' },
          body: { backgroundColor: palette.paper },
        }} />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: palette.paper,
          }}
        >
          <CircularProgress sx={{ color: palette.teal }} />
        </Box>
      </ThemeProvider>
    );
  }

  // ─── Invalid Token State ────────────────────────────────────────────────
  if (!tokenValid) {
    return (
      <ThemeProvider theme={lightOnlyTheme}>
        <GlobalStyles styles={{
          '@import': "url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap')",
          ':root': { colorScheme: 'light' },
          body: { backgroundColor: palette.paper },
        }} />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            bgcolor: palette.paper,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: 480,
              width: '100%',
              p: 4,
              borderRadius: 3,
              bgcolor: palette.card,
              textAlign: 'center',
              border: `1px solid ${palette.border}`,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#FEF2F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Lock sx={{ fontSize: 32, color: palette.red }} />
            </Box>

            <Typography
              sx={{
                fontFamily: "'Fraunces', serif",
                fontWeight: 500,
                color: palette.ink,
                fontSize: 24,
                mb: 1,
              }}
            >
              Invalid Reset Link
            </Typography>

            <Typography sx={{ color: palette.muted, mb: 3 }}>
              {tokenError || 'This password reset link is invalid or has expired.'}
            </Typography>

            <Button
              variant="contained"
              onClick={() => navigate('/forgot-password')}
              disableElevation
              sx={{
                py: 1.7,
                px: 4,
                borderRadius: '8px',
                bgcolor: palette.teal,
                '&:hover': { bgcolor: palette.tealDark },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 15.5,
              }}
            >
              Request New Reset Link
            </Button>

            <Box sx={{ mt: 2 }}>
              <Button
                component={RouterLink}
                to="/login"
                sx={{
                  color: palette.muted,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent', color: palette.teal },
                }}
              >
                ← Back to Login
              </Button>
            </Box>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  // ─── Main Render ──────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={lightOnlyTheme}>
      <GlobalStyles
        styles={{
          '@import':
            "url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap')",
          ':root': { colorScheme: 'light' },
          html: { colorScheme: 'light' },
          body: { colorScheme: 'light', backgroundColor: palette.paper },
        }}
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          bgcolor: palette.paper,
          colorScheme: 'light',
        }}
      >
        {/* ─── LEFT PANEL — "Live Ledger" ────────────────────────────────── */}
        {!isMobile && (
          <Box
            sx={{
              flex: '1 1 52%',
              maxWidth: '52%',
              bgcolor: palette.ledger,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: { md: 6, lg: 9 },
              py: 6,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Grid texture */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
                maskImage: 'radial-gradient(ellipse 75% 55% at 25% 15%, black, transparent)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: -180,
                left: -140,
                width: 480,
                height: 480,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${palette.tealSoft} 0%, transparent 70%)`,
                filter: 'blur(6px)',
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(15,118,110,0.16)',
                  border: '1px solid rgba(94,234,212,0.25)',
                  borderRadius: '999px',
                  px: 1.6,
                  py: 0.5,
                  mb: 4.5,
                }}
              >
                <FiberManualRecord sx={{ fontSize: 8, color: '#5EEAD4' }} />
                <Typography
                  sx={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11.5,
                    letterSpacing: '0.08em',
                    color: '#99F6E4',
                    fontWeight: 500,
                  }}
                >
                  SECURE PASSWORD RESET
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 500,
                  color: '#F5F4F0',
                  fontSize: { md: 33, lg: 40 },
                  lineHeight: 1.16,
                  letterSpacing: '-0.01em',
                  mb: 2.5,
                }}
              >
                Reset your password securely.
              </Typography>

              <Typography
                sx={{
                  color: 'rgba(226,232,240,0.72)',
                  fontSize: 16,
                  lineHeight: 1.7,
                  mb: 5,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {email ? (
                  <>We're resetting the password for <strong style={{ color: '#F5F4F0' }}>{email}</strong>. Enter a strong, unique password to secure your account.</>
                ) : (
                  'Enter a strong, unique password to secure your account. Make sure it\'s at least 6 characters long.'
                )}
              </Typography>

              {/* Ledger table */}
              <Box
                sx={{
                  border: `1px solid ${palette.ledgerLine}`,
                  borderRadius: '10px',
                  overflow: 'hidden',
                  mb: 5,
                  bgcolor: 'rgba(255,255,255,0.02)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 2.2,
                    py: 1.2,
                    borderBottom: `1px solid ${palette.ledgerLine}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10.5,
                      letterSpacing: '0.09em',
                      color: 'rgba(226,232,240,0.45)',
                    }}
                  >
                    TIMESTAMP
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10.5,
                      letterSpacing: '0.09em',
                      color: 'rgba(226,232,240,0.45)',
                    }}
                  >
                    Δ SCORE
                  </Typography>
                </Box>

                {ledger.map((row, i) => (
                  <Box
                    key={row.vendor}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2.2,
                      py: 1.3,
                      borderTop: i === 0 ? 'none' : `1px solid ${palette.ledgerLine}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 11.5,
                          color: 'rgba(226,232,240,0.4)',
                          flexShrink: 0,
                        }}
                      >
                        {row.time}
                      </Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: '#F1F0EC',
                            fontSize: 13.5,
                            fontWeight: 500,
                            lineHeight: 1.25,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.vendor}
                        </Typography>
                        <Typography sx={{ color: 'rgba(226,232,240,0.45)', fontSize: 11.5 }}>
                          {row.tag}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexShrink: 0, pl: 1.5 }}>
                      <Typography
                        sx={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 13,
                          fontWeight: 500,
                          color: row.delta < 0 ? '#F87171' : '#4ADE80',
                        }}
                      >
                        {row.delta > 0 ? `+${row.delta}` : row.delta}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${palette.ledgerLine}`,
                    borderRadius: '999px',
                    px: 1.4,
                    py: 0.6,
                  }}
                >
                  <Security sx={{ fontSize: 14, color: 'rgba(226,232,240,0.6)' }} />
                  <Typography sx={{ color: 'rgba(226,232,240,0.75)', fontSize: 12 }}>
                    End-to-End Encrypted
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${palette.ledgerLine}`,
                    borderRadius: '999px',
                    px: 1.4,
                    py: 0.6,
                  }}
                >
                  <VerifiedUser sx={{ fontSize: 14, color: 'rgba(226,232,240,0.6)' }} />
                  <Typography sx={{ color: 'rgba(226,232,240,0.75)', fontSize: 12 }}>
                    24-Hour Token Validity
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* ─── RIGHT PANEL — Reset Password Form ──────────────────────────── */}
        <Box
          sx={{
            flex: { xs: '1 1 100%', md: '1 1 48%' },
            maxWidth: { xs: '100%', md: '48%' },
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, md: 5 },
            bgcolor: palette.paper,
            minHeight: '100vh',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 480,
              p: 0,
              borderRadius: 3,
              bgcolor: 'transparent',
            }}
          >
            {/* Logo */}
            <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start', mb: isMobile ? 5 : 6 }}>
              <img
                src="/logo.png"
                alt="Platform logo"
                style={{ height: isMobile ? 84 : 108, width: 'auto' }}
              />
            </Box>

            <Box sx={{ mb: 5 }}>
              <Typography
                sx={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 500,
                  color: palette.ink,
                  fontSize: 30,
                  letterSpacing: '-0.01em',
                  mb: 1,
                }}
              >
                Create New Password
              </Typography>
              <Typography sx={{ color: palette.muted, fontSize: 15.5 }}>
                {email ? `For ${email}` : 'Enter your new password below.'}
              </Typography>
            </Box>

            {success ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: '#F0FDF4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <CheckCircle sx={{ fontSize: 40, color: palette.green }} />
                </Box>

                <Typography
                  sx={{
                    fontFamily: "'Fraunces', serif",
                    fontWeight: 500,
                    color: palette.ink,
                    fontSize: 24,
                    mb: 1,
                  }}
                >
                  Password Reset Successful! 🎉
                </Typography>

                <Typography sx={{ color: palette.muted, mb: 3 }}>
                  Your password has been successfully reset.
                  You'll be redirected to login shortly.
                </Typography>

                <CircularProgress sx={{ color: palette.teal }} size={28} />
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                    {error}
                  </Alert>
                )}

                <Typography
                  component="label"
                  htmlFor="new-password"
                  sx={{ display: 'block', fontSize: 13, fontWeight: 600, color: palette.inkSoft, mb: 0.8 }}
                >
                  New Password
                </Typography>
                <TextField
                  id="new-password"
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setError('');
                  }}
                  required
                  placeholder="••••••••"
                  sx={{ mb: 3, ...inputSx }}
                  helperText="Minimum 6 characters"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: palette.faint, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                          {showPassword ? (
                            <VisibilityOff sx={{ color: palette.faint, fontSize: 20 }} />
                          ) : (
                            <Visibility sx={{ color: palette.faint, fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography
                  component="label"
                  htmlFor="confirm-password"
                  sx={{ display: 'block', fontSize: 13, fontWeight: 600, color: palette.inkSoft, mb: 0.8 }}
                >
                  Confirm Password
                </Typography>
                <TextField
                  id="confirm-password"
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  required
                  placeholder="••••••••"
                  sx={{ mb: 4, ...inputSx }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: palette.faint, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                          {showConfirmPassword ? (
                            <VisibilityOff sx={{ color: palette.faint, fontSize: 20 }} />
                          ) : (
                            <Visibility sx={{ color: palette.faint, fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  disableElevation
                  sx={{
                    py: 1.7,
                    borderRadius: '8px',
                    bgcolor: palette.teal,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: palette.tealDark, boxShadow: 'none' },
                    '&.Mui-disabled': { bgcolor: '#D7D2C4', color: '#fff' },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 15.5,
                  }}
                >
                  {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Reset Password'}
                </Button>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    component={RouterLink}
                    to="/login"
                    startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                    sx={{
                      color: palette.muted,
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: 14.5,
                      '&:hover': { bgcolor: 'transparent', color: palette.teal },
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </form>
            )}
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
}